import React, { useEffect, useMemo } from "react";
import Spinner from "@shared/components/Spinner";
import Select from "@shared/components/Select";
import TopSlideFlashNotices from "@shared/components/TopSlideFlashNotices";

const NOTICE_DURATION_MS = 4500;

export function SortableTh({ label, sortKey, currentSort, currentOrder, onSort, className = "" }) {
  const isActive = currentSort === sortKey;
  const nextOrder = isActive && currentOrder === "asc" ? "desc" : "asc";
  return (
    <th scope="col" className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey, nextOrder)}
        className="group inline-flex items-center gap-1 text-left font-medium hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 rounded"
      >
        {label}
        {isActive ? (
          <span className="text-slate-500" aria-hidden>{currentOrder === "asc" ? "↑" : "↓"}</span>
        ) : (
          <span className="text-slate-300 group-hover:text-slate-400" aria-hidden>↕</span>
        )}
      </button>
    </th>
  );
}

function SearchInput({ value, onChange, placeholder = "Search...", id }) {
  return (
    <input
      id={id}
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full max-w-xs px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
      aria-label={placeholder}
    />
  );
}

const PER_PAGE_OPTIONS = [10, 25, 50].map((n) => ({ value: n, label: String(n) }));

function ChevronLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 01.02-.02z" clipRule="evenodd" />
    </svg>
  );
}

function ChevronRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.06-1.06l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-.02.02z" clipRule="evenodd" />
    </svg>
  );
}

function Pagination({ page, totalPages, totalCount, perPage, onPageChange, onPerPageChange }) {
  const from = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalCount);

  return (
    <div className="bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200">
      <div className="text-sm text-slate-600">
        Showing <span className="font-medium">{from}</span> to <span className="font-medium">{to}</span> of <span className="font-medium">{totalCount}</span>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-600" htmlFor="list-per-page">
          Per page
          <Select
            id="list-per-page"
            value={perPage}
            onChange={(v) => onPerPageChange(Number(v))}
            options={PER_PAGE_OPTIONS}
            placeholder="Per page"
            className="w-20 min-w-0"
          />
        </label>
        <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-inset"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-900 ring-1 ring-inset ring-slate-300 bg-white">
            Page {page} of {totalPages || 1}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-inset"
            aria-label="Next page"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );
}

const CTA_BG = "#003D4C";
const CTA_BORDER = "#004D5C";
const CTA_HOVER = "#004D5C";

export function CtaButton({ children, onClick, disabled, type = "button", className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004D5C] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ backgroundColor: CTA_BG, border: `1px solid ${CTA_BORDER}` }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = CTA_HOVER;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = CTA_BG;
      }}
    >
      <span aria-hidden>+</span>
      {children}
    </button>
  );
}

export function ListPageLayout({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  actions,
  notice,
  onNoticeDismiss,
  loading,
  error,
  totalCount,
  page,
  totalPages,
  perPage,
  onPageChange,
  onPerPageChange,
  children,
}) {
  useEffect(() => {
    if (!notice?.message) return undefined;
    const t = window.setTimeout(() => onNoticeDismiss?.(), NOTICE_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [notice, onNoticeDismiss]);

  const flashItems = useMemo(() => {
    const items = [];
    if (notice?.message) {
      items.push({
        key: `notice-${notice.message}`,
        variant: notice.type === "success" ? "success" : "error",
        text: notice.message,
      });
    }
    if (error) {
      items.push({
        key: `err-${error}`,
        variant: "error",
        text: error,
      });
    }
    return items;
  }, [notice, error]);

  return (
    <div className="space-y-4">
      <TopSlideFlashNotices
        items={flashItems}
        wrapperClassName="pointer-events-none fixed top-20 z-40 flex flex-col items-center gap-2 px-4 pt-2 left-0 right-0"
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4 flex-1 min-w-0">
          {title ? <h2 className="text-xl font-semibold text-slate-800 shrink-0">{title}</h2> : null}
          <SearchInput
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            id="list-search"
          />
          {loading && (
            <span className="flex items-center gap-2 text-sm text-slate-500" role="status" aria-live="polite">
              <Spinner size={18} />
              <span>Updating…</span>
            </span>
          )}
        </div>
        {actions != null && <div className="shrink-0">{actions}</div>}
      </div>

      {loading && totalCount === 0 && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Spinner size={32} />
          <span className="sr-only">Loading…</span>
        </div>
      )}

      {(loading && totalCount > 0) || (!loading && !error) ? (
        <>
          {!loading && totalCount === 0 ? (
            <p className="py-6 text-center text-slate-500 text-sm">No results found.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              {children}
            </div>
          )}
          {!loading && totalCount > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              perPage={perPage}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
            />
          )}
          {loading && totalCount > 0 && (
            <div className="flex items-center justify-center py-3 text-sm text-slate-500">
              <Spinner size={20} />
              <span className="ml-2">Updating results…</span>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
